import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS, U2101ROLES } from "../../../../U2101";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { U2000IdHeaderCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { emptyLogicalUnitPersonalization, type LogicalUnitPersonalization } from "../../../../U2101Service";
import type { U2101LogicalUnitFormExtendedHandlers } from "../../../U2101LogicalUnitForm";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { u2101LogicalUnitPersonalizationHandler as u2101LogicalUnitPersonalizationHandler } from "./U2101LogicalUnitPersonalizationHandlers";
import U2101LogicalUnitPersonalizationEditDlg from "./U2101LogicalUnitPersonalizationEditDlg";
import U2101LogicalUnitPersonalizationDeleteDlg from "./U2101LogicalUnitPersonalizationDeleteDlg";

// Typage des props du composant
interface U2101LogicalUnitPersonnalizationBodyProps {
  logicalUnitFormHandlers: U2101LogicalUnitFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "id",
      label: "",
      widthClass: "w-[50px]",
      type: "number",
      align: "center",
      headerDisplay: (column: any, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
        return <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />;
      },
      display: (column: any, value: any, row: any, allowRowSelection: boolean, rowUpdated?: (row: any) => void) => {
        return <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allowRowSelection} />;
      },
      isCriteriaField: false,
    },
    {
      name: "logicalUnitPersonalizationCode",
      label: t("lblLogicalUnitPersonalizationCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[225px]",
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitPersonalizationDescription",
      label: t("lblLogicalUnitPersonalizationDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitPersonalizationValue",
      label: t("lblLogicalUnitPersonalizationValue", { ns: NS }),
      type: "string",
      align: "left",
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U2101LogicalUnitPersonalizationsBody: React.FC<U2101LogicalUnitPersonnalizationBodyProps> = ({ logicalUnitFormHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const gridStateHandlers = useU2000GridStateHandlers<LogicalUnitPersonalization>();

  const validateLogicalUnitPersonalization = (logicalUnitPersonalization: LogicalUnitPersonalization) => {
    const result: Record<string, string> = {};
    if (!logicalUnitPersonalization.logicalUnitPersonalizationCode) result.logicalUnitPersonalizationCode = t("errLogicalUnitPersonalizationCodeRequired", { ns: NS });
    if (!logicalUnitPersonalization.logicalUnitPersonalizationDescription) {
      result.logicalUnitPersonalizationDescription = t("errLogicalUnitPersonalizationDescriptionRequired", { ns: NS });
    }
    if (!logicalUnitPersonalization.logicalUnitPersonalizationValue) {
      result.logicalUnitPersonalizationValue = t("errLogicalUnitPersonalizationValueRequired", { ns: NS });
    }

    // no duplicate Logical Unit Actions (by logicalUnitActionCode)
    const duplicate = gridStateHandlers
      .getData()
      .find((item) => item.logicalUnitPersonalizationCode === logicalUnitPersonalization.logicalUnitPersonalizationCode && item.id !== logicalUnitPersonalization.id);
    if (duplicate) {
      result.logicalUnitPersonalizationCode = t("errLogicalUnitPersonalizationDuplicate", { ns: NS });
    }

    return result;
  };

  const u2101LogicalUnitPersonalizationBL: U2000FormSBusinessHandlers<LogicalUnitPersonalization> = {
    newRecord: () => {
      return emptyLogicalUnitPersonalization;
    },

    // validation du formulaire
    validateForm: validateLogicalUnitPersonalization,
  };

  const u2101LogicalUnitPersonalizationformStateHandlers = useU2000FormStateHandlers<LogicalUnitPersonalization>({
    formState,
    formStateDispatch,
    businessHandlers: u2101LogicalUnitPersonalizationBL,
  });

  const u2101LogicalUnitPersonalizationHandlers = u2101LogicalUnitPersonalizationHandler({ gridStateHandlers, t, formStateHandlers: u2101LogicalUnitPersonalizationformStateHandlers });

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitPersonalizations
  useEffect(() => {
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
    const logicalUnitPersonalizations = logicalUnitFormHandlers.getData?.()?.logicalUnitPersonalizations;
    gridStateHandlers.setData(Array.isArray(logicalUnitPersonalizations) ? logicalUnitPersonalizations : []);
    gridStateHandlers.setTotalRows(Array.isArray(logicalUnitPersonalizations) ? logicalUnitPersonalizations.length : 0);
  }, [logicalUnitFormHandlers, t]);

  useEffect(() => {
    if (u2101LogicalUnitPersonalizationHandlers.dataChangedEvent === 0) return;
    const logicalUnitPersonalizations = gridStateHandlers.getData();
    logicalUnitFormHandlers.setData({
      ...logicalUnitFormHandlers.getData?.(),
      logicalUnitPersonalizations: logicalUnitPersonalizations,
    });
  }, [u2101LogicalUnitPersonalizationHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return logicalUnitFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return logicalUnitFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false;
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2101LogicalUnitPersonalizationHandlers.onAdd,
        hasRole: hasRole(U2101ROLES.ACTIONS),
        disabled: logicalUnitFormHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u2101LogicalUnitPersonalizationHandlers.onModify,
        hasRole: hasRole(U2101ROLES.ACTIONS),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u2101LogicalUnitPersonalizationHandlers.onDelete,
        hasRole: hasRole(U2101ROLES.ACTIONS),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2101LogicalUnitPersonalizationHandlers]
  );

  // Render
  return (
    <>
      <U2101LogicalUnitPersonalizationEditDlg u2101LogicalUnitPersonalizationHandler={u2101LogicalUnitPersonalizationHandlers} t={t} NS={NS} />
      <U2101LogicalUnitPersonalizationDeleteDlg u2101LogicalUnitPersonalizationHandler={u2101LogicalUnitPersonalizationHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={logicalUnitFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblLogicalUnitPersonalization", {
          count: u2101LogicalUnitPersonalizationHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2101LogicalUnitPersonalizationsBody;
